import { memo, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputLabel,
  TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT, MAP_DEFAULT_LAT, MAP_DEFAULT_LONG } from '~/utils/constants/common';
import { handleApiError, notify, getClientGeolocation, cast, catchError } from '~/utils/helper';
import { CloseIcon } from '~/assets/icons';
import { IEditTimeClock, ITimeClockEvent } from '~/utils/interface/timeClock';
import { BaseRow, BaseSelect, LabelTimeHour } from '~/components/common';
import { Map, Marker } from '@vis.gl/react-google-maps';
import { handleCheckinTimeClock, handleUpdateTimeClock } from '~/thunks/time-clocks/timeClocksThunk';
import { ITaskSelection } from '~/utils/interface/task';
import dayjs from 'dayjs';
import {
  EDIT_OPTION_BREAK_IN,
  EDIT_OPTION_BREAK_OUT,
  EDIT_OPTION_CHECK_OUT,
  TIME_CLOCK_STATUS_BREAK,
  TIME_CLOCK_STATUS_COMPLETED,
  TIME_CLOCK_STATUS_PROGRESS,
} from '~/utils/constants/time-clock';
import jobApi from '~/apis/job';
import { IJobSelection } from '~/utils/interface/job';

type EditTimeClockDialogProps = {
  open: boolean;
  setOpen: (open: boolean, needRefreshData?: boolean) => void;
  mode: string;
  data?: ITimeClockEvent;
};

const initialValues: IEditTimeClock = {
  taskId: '',
  jobId: '',
  lat: 0,
  long: 0,
  location: '',
  option: '',
};

const EditTimeClockDialog = (props: EditTimeClockDialogProps) => {
  //#region Destructuring Props
  const { mode = DIALOG_MODE_CREATE } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/time-clocks', 'admin/common']);
  const dispatch = useAppDispatch();
  const form = useFormik({
    validateOnChange: false, // Prevent form reset API error
    initialValues,
    onSubmit: async (values, { setFieldError }) => {
      if (mode === DIALOG_MODE_CREATE) {
        let data = {
          taskId: values.taskId,
          jobId: values.jobId,
          lat: selectedTask?.lat,
          long: selectedTask?.long,
          location: selectedTask?.location,
        };

        try {
          const clientLocation = await getClientGeolocation();
          data = { ...data, ...clientLocation };
        } catch (e: any) {
          if (!process.env.REACT_APP_BYPASS_CHECKIN) {
            notify('error', t(`locationError.${e.code}`, { ns: 'admin/common' }));
            return false;
          }
        }

        try {
          await dispatch(handleCheckinTimeClock(data)).unwrap();
          notify('success', t('editTimeClockDialog.checkInSuccess'));
          handleSetOpen(false, true);
        } catch (e) {
          handleApiError(e, setFieldError);
        }
        return;
      }

      if (mode === DIALOG_MODE_EDIT) {
        try {
          const data: IEditTimeClock = {
            taskId: values.taskId,
            jobId: values.jobId,
          };

          if (values.option) data.option = values.option;

          await dispatch(handleUpdateTimeClock({ id: cast(props.data?.id), data })).unwrap();
          notify('success', t('editSuccess', { ns: 'admin/common' }));

          handleSetOpen(false, true);
        } catch (e) {
          handleApiError(e, setFieldError);
        }
      }
    },
  });
  //#endregion Declare Hook
  const { profile } = useAppSelector((state) => state.auth);
  const { taskSelectionList } = useAppSelector((state) => state.task);
  const [projectsSelection, setProjectsSelection] = useState<Array<IJobSelection>>([]);

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [selectedTask, setSelectedTask] = useState<ITaskSelection>();
  //#endregion Declare State

  //#region Implement Hook
  useEffect(() => {
    if (form.values.taskId) {
      setSelectedTask(taskSelectionList.find((item) => item.id === form.values.taskId));
    }
  }, [form.values.taskId, taskSelectionList]);

  useEffect(() => {
    if (props.data) {
      form.setFieldValue('taskId', props.data.taskId);
      form.setFieldValue('jobId', props.data.jobId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.data]);

  useEffect(() => {
    getJobSelections();
  }, []);
  //#endregion Implement Hook

  //#region Handle Function
  const handleSetOpen = (open: boolean, needRefreshData?: boolean) => {
    form.resetForm();
    props.setOpen(open, needRefreshData);
    setSelectedTask(undefined);
  };

  const handleClickBreakIn = () => {
    form.setFieldValue('option', EDIT_OPTION_BREAK_IN);
    form.handleSubmit();
  };
  const getJobSelections = async () => {
    try {
      const response = await jobApi.getJobsSelection();

      setProjectsSelection(response.data.data.responses);
    } catch (error: unknown) {
      catchError(error, (message) => {
        notify('error', message);
      });
    }
  };
  const handleClickBreakOut = () => {
    form.setFieldValue('option', EDIT_OPTION_BREAK_OUT);
    form.handleSubmit();
  };

  const handleClickCheckOut = () => {
    form.setFieldValue('option', EDIT_OPTION_CHECK_OUT);
    form.handleSubmit();
  };

  const renderMarkerComponent = () => {
    return (
      <Marker
        position={{ lat: selectedTask?.lat ?? 0, lng: selectedTask?.long ?? 0 }}
        label={{
          text: selectedTask?.location ?? '',
          className:
            'bg-[#FFFFFF] p-[8px] absolute bottom-[-10px] left-[-5px] rounded-b-[6px] rounded-tr-[6px] border-[0.79px] border-[#E5EAEF]',
        }}
      />
    );
  };
  //#endregion Handle Function

  return (
    <Dialog open={props.open} onClose={() => handleSetOpen(false)} fullWidth maxWidth='md'>
      <DialogTitle>
        {mode === DIALOG_MODE_CREATE ? t('editTimeClockDialog.addTitle') : t('editTimeClockDialog.editTitle')}
        <IconButton onClick={() => handleSetOpen(false)} className='absolute right-[10px] top-[10px]'>
          <CloseIcon className='w-[10px] h-[10px]' />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box component='form'>
          {mode === DIALOG_MODE_EDIT && (
            <BaseRow className='mb-[20px] gap-[20px]'>
              <Box className='w-full'>
                <InputLabel>{t('editTimeClockDialog.checkIn')}</InputLabel>
                <TextField
                  className='bg-[#F6F5F5] rounded-[8px]'
                  value={dayjs(props.data?.checkIn).format('hh:mm A')}
                  disabled
                />
              </Box>
              <Box className='w-full'>
                <InputLabel>{t('duration')}</InputLabel>
                <Box className='border border-[#E5EAEF] px-[15px] py-[10px] bg-[#F6F5F5] rounded-[8px] text-[#A2A7B4]'>
                  {<LabelTimeHour zeroValue='-' time={cast(props.data?.totalTimeWork)} />}
                </Box>
              </Box>
              <Box className='w-full'>
                <InputLabel>{t('editTimeClockDialog.totalBreak')}</InputLabel>
                <Box className='border border-[#E5EAEF] px-[15px] py-[10px] bg-[#F6F5F5] rounded-[8px] text-[#A2A7B4]'>
                  {<LabelTimeHour zeroValue='-' time={cast(props.data?.totalTimeBreak)} />}
                </Box>
              </Box>
            </BaseRow>
          )}
          <Box className='mb-[20px]'>
            <InputLabel>{t('editTimeClockDialog.projectName')}</InputLabel>

            <BaseSelect
              name='jobId'
              placeholder={t('editTimeClockDialog.projectName')}
              value={form.values.jobId}
              options={projectsSelection}
              onChange={form.handleChange}
              error={Boolean(form.errors.jobId)}
              helperText={form.errors.jobId}
            />
          </Box>
          <Box className='mb-[20px]'>
            <InputLabel>{t('editTimeClockDialog.selectTask')}</InputLabel>
            <BaseSelect
              name='taskId'
              options={taskSelectionList}
              value={form.values.taskId}
              onChange={form.handleChange}
              error={Boolean(form.errors?.taskId)}
              helperText={form.errors?.taskId}
            ></BaseSelect>
          </Box>

          <BaseRow className='mb-[20px] gap-[20px]'>
            <Box className='w-full'>
              <InputLabel>{t('editTimeClockDialog.userName')}</InputLabel>
              <TextField placeholder={t('editTimeClockDialog.userName')} value={cast(profile?.name)} disabled />
            </Box>

            <Box className='w-full'>
              <InputLabel>{t('editTimeClockDialog.taskName')}</InputLabel>
              <TextField placeholder={t('editTimeClockDialog.taskName')} value={cast(selectedTask?.name)} disabled />
            </Box>
          </BaseRow>

          <Box className='mb-[20px]'>
            <InputLabel>{t('editTimeClockDialog.mapView')}</InputLabel>
            <Box>
              <Map
                className='h-[400px] w-full'
                center={{
                  lat: selectedTask?.lat ?? MAP_DEFAULT_LAT,
                  lng: selectedTask?.long ?? MAP_DEFAULT_LONG,
                }}
                defaultZoom={5}
                gestureHandling={'greedy'}
                disableDefaultUI={true}
                mapId={process.env.REACT_APP_GOOGLE_MAPS_ID}
              >
                {selectedTask && renderMarkerComponent()}
              </Map>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        {mode === DIALOG_MODE_CREATE ? (
          <>
            <Button color='secondary' variant='contained' onClick={() => handleSetOpen(false)}>
              {t('cancel', { ns: 'admin/common' })}
            </Button>
            <Button color='primary' variant='contained' onClick={() => form.handleSubmit()}>
              {t('editTimeClockDialog.checkIn')}
            </Button>
          </>
        ) : (
          <>
            {props.data?.status === TIME_CLOCK_STATUS_PROGRESS && (
              <Button color='secondary' variant='contained' onClick={handleClickBreakIn}>
                {t('editTimeClockDialog.break')}
              </Button>
            )}
            {props.data?.status === TIME_CLOCK_STATUS_BREAK && (
              <Button color='secondary' variant='contained' onClick={handleClickBreakOut}>
                {t('editTimeClockDialog.breakOut')}
              </Button>
            )}
            {props.data?.status !== TIME_CLOCK_STATUS_COMPLETED && (
              <Button color='primary' variant='contained' onClick={handleClickCheckOut}>
                {t('editTimeClockDialog.checkOut')}
              </Button>
            )}
            <Button color='primary' variant='contained' onClick={() => form.handleSubmit()}>
              {t('save', { ns: 'admin/common' })}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default memo(EditTimeClockDialog);
